import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { ROLES } from '../../../constants';
import {
  callGetUserPermissions,
  getActiveUserRoleIds,
  getRolePermissionsByRoleIds,
} from '../../../services/api/account';

export const PermissionsContext = React.createContext();

export function usePermissions() {
  return useContext(PermissionsContext);
}

export function PermissionsProvider({ children }) {
  const { 
    currentUser, 
    profile,
    role: authRole,
    userRoles = [], 
    maxRoleLevel = 1,
    isPassenger: authIsPassenger,
    isDriver: authIsDriver,
    isAdmin: authIsAdmin,
    isSuperAdmin: authIsSuperAdmin
  } = useAuth();
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(() => !!currentUser);

  const clearState = useCallback(() => {
    setUserPermissions([]);
  }, []);

  const loadPermissions = useCallback(async () => {
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      clearState();
      setLoading(false);
    }, 6000);

    try {
      setLoading(true);

      if (!currentUser) {
        clearState();
        clearTimeout(timeout);
        setLoading(false);
        return;
      }

      const user = currentUser;
      let permissionsLoaded = false;

      const withTimeout = (promise, ms = 5000) =>
        Promise.race([
          promise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))
        ]);

      try {
        const { data: permissions } = await withTimeout(
          callGetUserPermissions(user.id)
        );
        if (permissions) {
          setUserPermissions(permissions);
          permissionsLoaded = true;
        }
      } catch (err) {
        // Fallback silencioso rápido
      }

      if (timedOut) { clearTimeout(timeout); return; }

      if (!permissionsLoaded) {
        try {
          const { data: urData } = await withTimeout(
            getActiveUserRoleIds(user.id)
          );

          if (urData && urData.length > 0) {
            const roleIds = urData.map(r => r.role_id);
            const { data: rpData } = await withTimeout(
              getRolePermissionsByRoleIds(roleIds)
            );

            if (rpData) {
              const unique = {};
              rpData.forEach(rp => {
                const p = rp.permissions;
                if (p && p.code && !unique[p.code]) {
                  unique[p.code] = {
                    permission_name: p.code,
                    display_name: p.description || p.code,
                    module: p.code.split('.')[0] || 'general',
                    action: p.code.split('.')[1] || 'access',
                    resource: p.code,
                  };
                }
              });
              setUserPermissions(Object.values(unique));
            }
          }
        } catch (fbErr) {
          // Fallback silencioso
        }
      }
    } catch (e) {
      clearState();
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  }, [currentUser, clearState]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions, currentUser]);

  // Unified boolean API (synced with AuthContext fallbacks to prevent permission blocks)
  const isAdmin      = authIsAdmin;
  const isSuperAdmin = authIsSuperAdmin;
  // RBAC unificado (SPEC-003): solo roles ACTIVOS cuentan. Sin descartes.
  const isDriver     = authIsDriver || (userRoles && userRoles.some(r => (r.role_name === ROLES.DRIVER || r.role_name === 'drivers') && r.is_active !== false));
  const isPassenger  = authIsPassenger || (userRoles && userRoles.some(r => r.role_name === ROLES.PASSENGER && r.is_active !== false));
  const isSupport    = userRoles && userRoles.some(r => r.role_name === ROLES.SUPPORT && r.is_active !== false);

  const hasPermission = useCallback((permissionName) => {
    if (!permissionName) return false;
    if (isSuperAdmin) return true;
    return userPermissions.some(p => {
      if (typeof p === 'string') return p === permissionName;
      return (
        p.permission_code === permissionName ||
        p.permission_name === permissionName ||
        p.code === permissionName ||
        p.name === permissionName
      );
    });
  }, [userPermissions, isSuperAdmin]);

  const hasModulePermission = useCallback((module, action = null) => {
    if (isSuperAdmin) return true;
    return userPermissions.some(p => {
      const matchesModule = p.module === module;
      const matchesAction = !action || p.action === action;
      return matchesModule && matchesAction;
    });
  }, [userPermissions, isSuperAdmin]);

  const hasRole = useCallback((roleName) => {
    if (!roleName) return false;
    // RBAC unificado (SPEC-003): solo user_roles ACTIVOS autorizan.
    // profiles.role es cache derivada: no re-derivar aqui.
    return userRoles.some(r => r.role_name === roleName && r.is_active !== false);
  }, [userRoles]);

  const hasMinLevel = useCallback((level) => {
    return maxRoleLevel >= level;
  }, [maxRoleLevel]);

  const getHighestRole = useCallback(() => {
    if (!userRoles || userRoles.length === 0) return null;
    return userRoles.reduce((highest, current) =>
      current.level > highest.level ? current : highest
    );
  }, [userRoles]);

  // ── Granular `can` helpers ─────────────────────────────────────────────────
  const can = React.useMemo(() => ({
    // Service-related permissions - FIXED: drivers can only view/accept, passengers can only request
    createService:          () => isPassenger || isAdmin || isSuperAdmin || hasPermission('services.create') || hasPermission('service.create'),
    viewOwnServices:        () => isPassenger || isAdmin || isSuperAdmin || hasPermission('services.read') || hasPermission('service.read.own'),
    viewAllServices:        () => isAdmin || isSuperAdmin || hasPermission('services.read_all') || hasPermission('service.read.all'),
    updateOwnService:       () => isPassenger || isAdmin || isSuperAdmin || hasPermission('services.update') || hasPermission('service.update.own'),
    cancelOwnService:       () => isPassenger || isAdmin || isSuperAdmin || hasPermission('services.update') || hasPermission('services.cancel') || hasPermission('service.cancel.own'),

    // Driver-specific permissions - drivers can only view and accept services
    viewAvailableServices:  () => isDriver || isAdmin || isSuperAdmin || hasPermission('driver.view.available') || hasPermission('services.read'),
    acceptService:          () => isDriver || hasPermission('driver.accept.service') || hasPermission('services.update'),
    updateTracking:         () => isDriver || hasPermission('driver.tracking.update') || hasPermission('tracking.read'),

    // Driver registration and profile management
    registerAsDriver:       () => true,
    updateDriverProfile:    () => isDriver || hasPermission('driver.profile.update.own') || hasPermission('drivers.profile.update.own'),

    // User profile permissions
    viewOwnProfile:         () => true,
    updateOwnProfile:       () => isPassenger || isDriver || isAdmin || isSuperAdmin || hasPermission('profile.update.own'),
    viewAllUsers:           () => isAdmin || isSuperAdmin || hasPermission('users.read_all') || hasPermission('user.read.all'),
    updateAnyUser:          () => isAdmin || isSuperAdmin || hasPermission('users.manage_roles') || hasPermission('users.ban') || hasPermission('user.update.all'),
    assignRoles:            () => isAdmin || isSuperAdmin || hasPermission('users.manage_roles') || hasPermission('user.role.assign'),
    blockUsers:             () => isAdmin || isSuperAdmin || hasPermission('users.ban') || hasPermission('user.block'),
    deleteUser:             () => isAdmin || isSuperAdmin || hasPermission('users.delete') || hasPermission('user.delete'),

    // Admin and system permissions
    viewDashboard:          () => isAdmin || isSuperAdmin || hasPermission('admin.dashboard') || hasPermission('analytics.view'),
    viewAnalytics:          () => isAdmin || isSuperAdmin || hasPermission('admin.analytics') || hasPermission('analytics.view'),
    manageSettings:        () => isAdmin || isSuperAdmin || hasPermission('admin.settings') || hasPermission('system.admin'),
    manageTariffs:          () => isAdmin || isSuperAdmin || hasPermission('admin.tariffs') || hasPermission('tariffs.manage'),

    // Support permissions
    viewTickets:            () => isSupport || isAdmin || isSuperAdmin || hasPermission('support.tickets.read'),
    respondTickets:         () => isSupport || isAdmin || isSuperAdmin || hasPermission('support.tickets.respond'),
    viewUsersForSupport:    () => isSupport || isAdmin || isSuperAdmin || hasPermission('support.users.view'),

    // Reviews
    createReview:           () => isPassenger || isAdmin || isSuperAdmin || hasPermission('review.create.own') || hasPermission('reviews.read_all'),
    viewOwnReviews:         () => isPassenger || isAdmin || isSuperAdmin || hasPermission('review.read.own'),
    viewReceivedReviews:    () => isDriver || isAdmin || isSuperAdmin || hasPermission('review.read.received'),

    // System maintenance
    systemMaintenance:      () => isSuperAdmin || hasPermission('super.system.maintenance'),
    databaseAccess:         () => isSuperAdmin || hasPermission('super.database.access'),

    // Driver-specific validation - only drivers can view driver-related data
    viewDriverProfile:      () => isDriver || isAdmin || isSuperAdmin || hasPermission('driver.view'),
  }), [hasPermission, isPassenger, isDriver, isAdmin, isSuperAdmin, isSupport]);

  const value = {
    userPermissions,
    loading,
    hasPermission,
    hasModulePermission,
    hasRole,
    hasMinLevel,
    isAdmin,
    isSuperAdmin,
    isDriver,
    isPassenger,
    isSupport,
    getHighestRole,
    can,
    refreshPermissions: loadPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}
